import { useMemo } from 'react'
import { createColumnHelper } from '@tanstack/react-table'
import clsx from 'clsx'

import { DISRUPTIONS_COLUMNS, MANDATORY_DISRUPTION_FIELDS } from '../constant'
import { Disruption } from '@/types/disruptions'
import { CommonHeader, DateHeader, filterByDateAndTime, SortHeader } from '@/ui/Table'
import { Status } from '../ui/components/Status'
import { sortDisruptionsByStatus } from '../helpers/sortByStatus'
import { useAuthStore } from '@/store/auth-store'
import { formatDisruptionDate } from '../helpers/formatDisruptionDate'
import { Actions } from '../ui/components/Actions'
import { sortDisruptionsByRoutes } from '../helpers/sortByRoutes'
import { extractParts, compareValueWithPrefix } from '@/helpers/compareValueWithPrefix'
import { generateDisruptionTimeOptions } from '../helpers/generateDisruptionTimeOptions'

import styles from '../ui/components/DisruptionsTable.module.scss'
import { MissingDisruptionInfo } from '../ui/components/MissingDisruptionInfo'

export const useDisruptionsColumns = () => {
    const columnHelper = createColumnHelper<Disruption>()
    const { role } = useAuthStore()

    return useMemo(
        () => [
            columnHelper.accessor('status', {
                id: DISRUPTIONS_COLUMNS.Status,
                header: (context) => <SortHeader {...context} buttonLabel="Sort by status" label="Status" />,
                cell: (info) => (info.getValue() ? <Status status={info.getValue()} /> : '-'),
                meta: {
                    alignCenter: true,
                    width: '7.14rem',
                },
                sortingFn: sortDisruptionsByStatus,
                enableSorting: true,
            }),
            columnHelper.accessor('reference', {
                id: DISRUPTIONS_COLUMNS.Reference,
                header: 'Reference',
                cell: (info) => <span className={clsx(styles.textBold)}>{info.getValue().replace('/', '/\n')}</span>,
                enableSorting: false,
                meta: {
                    alignCenter: true,
                    width: '7.142rem',
                },
            }),
            columnHelper.accessor((row) => row.routes.flatMap((item) => item.route), {
                id: DISRUPTIONS_COLUMNS.Routes,
                header: (context) => (
                    <CommonHeader
                        {...context}
                        buttonLabel="Sort by routes"
                        searchPlaceholder="Type to search for routes"
                        label="Routes"
                    />
                ),
                cell: (info) => (
                    <span className={clsx(styles.textBold, styles.routes)}>{info.getValue().join(', ')}</span>
                ),
                meta: {
                    alignCenter: true,
                    width: '8.571rem',
                    formatFilterValues: (routes) =>
                        routes.sort((routeA, routeB) => {
                            const [prefixA, numA, suffixA] = extractParts(routeA)
                            const [prefixB, numB, suffixB] = extractParts(routeB)

                            return compareValueWithPrefix([prefixA, numA, suffixA], [prefixB, numB, suffixB])
                        }),
                },
                filterFn: (row, columnId, filterValue) => {
                    const rowValue = row.getValue(columnId) as string[]
                    if (!rowValue || !Array.isArray(rowValue)) return false

                    const normalizedFilterValues = filterValue.map((val: string) => val.toLowerCase())
                    return rowValue.some((item) => normalizedFilterValues.includes(item.toLowerCase()))
                },
                sortingFn: sortDisruptionsByRoutes,
            }),
            columnHelper.accessor('location', {
                id: DISRUPTIONS_COLUMNS.Location,
                header: 'Location',
                cell: (info) =>
                    info.getValue() ? (
                        info.getValue()
                    ) : (
                        <MissingDisruptionInfo label={MANDATORY_DISRUPTION_FIELDS.Location} />
                    ),
                enableSorting: false,
                meta: {
                    width: '17.142rem',
                },
            }),
            columnHelper.accessor((row) => row.routes.filter((route) => route.diversion), {
                id: DISRUPTIONS_COLUMNS.Diversion,
                header: 'Details',
                cell: (info) => (
                    <>
                        {info.row.original.diversion ? (
                            <p className={styles.details}>{info.row.original.diversion}</p>
                        ) : (
                            <MissingDisruptionInfo label={MANDATORY_DISRUPTION_FIELDS.Details} />
                        )}
                        {info.getValue().map((value, index) => (
                            <p key={index} className={styles.details}>
                                {value.route}
                                {'\u00A0\u00A0\u00A0'}
                                {value.diversion}
                            </p>
                        ))}
                    </>
                ),
                enableSorting: false,
                meta: {
                    width: '100%',
                },
            }),
            columnHelper.accessor('disruptionType', {
                id: DISRUPTIONS_COLUMNS.DisruptionType,
                header: 'Disruption type',
                enableSorting: false,
                cell: (info) => (info.getValue() ? info.getValue() : '-'),
                meta: {
                    width: '15.714rem',
                },
            }),
            columnHelper.accessor('nmccInformed', {
                id: DISRUPTIONS_COLUMNS.NmccInformed,
                header: 'NMCC informed',
                enableSorting: false,
                cell: (info) => (info.getValue() === null ? '-' : info.getValue() ? 'Yes' : 'No'),
                meta: {
                    width: '8.571rem',
                },
            }),
            columnHelper.accessor('delay', {
                id: DISRUPTIONS_COLUMNS.Delay,
                header: 'Delay',
                cell: (info) => (info.getValue() ? `${info.getValue()} min` : '-'),
                enableSorting: false,
                meta: {
                    alignCenter: true,
                    width: '5.5rem',
                },
            }),
            columnHelper.accessor('start', {
                id: DISRUPTIONS_COLUMNS.Start,
                header: (context) => <DateHeader {...context} label="Start" customTimeOptionsGenerator={generateDisruptionTimeOptions} />,
                cell: (info) => (
                    <span className={styles.time}>
                        {info.getValue() ? (
                            formatDisruptionDate(info.getValue())
                        ) : (
                            <MissingDisruptionInfo label={MANDATORY_DISRUPTION_FIELDS.Start} />
                        )}
                    </span>
                ),
                filterFn: filterByDateAndTime,
                meta: {
                    alignCenter: true,
                    width: '6.6rem',
                },
            }),
            columnHelper.accessor('end', {
                id: DISRUPTIONS_COLUMNS.End,
                header: (context) => <DateHeader {...context} label="End" customTimeOptionsGenerator={generateDisruptionTimeOptions} disableFutureDates={false} />,
                cell: (info) => (
                    <span className={styles.time}>{info.getValue() ? formatDisruptionDate(info.getValue()) : '-'}</span>
                ),
                filterFn: filterByDateAndTime,
                meta: {
                    alignCenter: true,
                    width: '6.6rem',
                },
            }),
            columnHelper.display({
                id: DISRUPTIONS_COLUMNS.Actions,
                cell: (context) => <Actions {...context} />,
                meta: {
                    width: 'var(--size-40)',
                },
            }),
        ],
        [role]
    )
}
